"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { buttonClasses } from "./Button";

/**
 * Bouton d'entrée dans la visite virtuelle : fondu de sortie de la page
 * d'accueil (~400 ms, voile basalte) avant la navigation - le lecteur 360°
 * affiche le même fond sombre à l'arrivée, donc aucun écran blanc.
 * Respecte prefers-reduced-motion (navigation immédiate) et laisse le
 * navigateur gérer les ouvertures en nouvel onglet (clic modifié).
 */
export default function EnterExperienceLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  const router = useRouter();
  const [leaving, setLeaving] = useState(false);

  // Préchargement de la page cible : la navigation après le fondu est instantanée
  useEffect(() => {
    router.prefetch(href);
  }, [router, href]);

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (leaving) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      router.push(href);
      return;
    }
    setLeaving(true);
    window.setTimeout(() => router.push(href), 400);
  };

  return (
    <>
      <Link href={href} onClick={onClick} className={buttonClasses("or")}>
        {children}
      </Link>
      {leaving && (
        <div
          aria-hidden
          className="fixed inset-0 z-[90] bg-basalte"
          style={{ animation: "fade-in 0.4s ease-out both" }}
        />
      )}
    </>
  );
}
